import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { useDarkMode } from "../../contexts/DarkModeContext";
import "./Explore.css";
import "../SavedProfiles/SavedProfiles.css";

const serviceCategories = [
  { label: "All", value: "All" },
  { label: "Speaking", value: "speaking" },
  { label: "Music", value: "music" },
  { label: "Teaching", value: "teaching" },
  { label: "Youth", value: "youth" },
  { label: "Children", value: "children" },
  { label: "Pastoral", value: "pastoral" },
  { label: "Admin", value: "admin" },
  { label: "Tech", value: "tech" },
  { label: "Events", value: "events" },
  { label: "Outreach", value: "outreach" },
  { label: "Prayer", value: "prayer" },
  { label: "Mentoring", value: "mentoring" },
];

const SkeletonCard = ({ isDarkMode }) => (
  <div className={`explore-card skeleton-card${isDarkMode ? " dark" : ""}`}>
    <div className="skeleton skeleton-avatar" />
    <div className="skeleton-body">
      <div className="skeleton skeleton-line skeleton-name" />
      <div className="skeleton skeleton-line skeleton-sub" />
      <div className="skeleton skeleton-line skeleton-bio" />
      <div className="skeleton skeleton-line skeleton-bio short" />
    </div>
  </div>
);

const Explore = () => {
  const { isDarkMode } = useDarkMode();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedLocation, setSelectedLocation] = useState("All");
  const [locations, setLocations] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [savingId, setSavingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const { data, error } = await supabase
          .from("user_profiles")
          .select("location")
          .eq("skill_provider", true)
          .eq("reference_status", "confirmed")
          .or("account_status.is.null,account_status.neq.deleted")
          .not("full_name", "is", null)
          .neq("full_name", "")
          .not("location", "is", null)
          .neq("location", "");

        if (error) throw error;

        const uniqueLocations = [
          ...new Set(data.map((u) => u.location?.trim()).filter(Boolean)),
        ].sort();
        setLocations(uniqueLocations);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };

    fetchLocations();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);

      try {
        let query = supabase
          .from("user_profiles")
          .select(
            `user_id, full_name, profile_picture_url, location, church_name,
             bio2, bio1, service_types, ways_to_help, skill_provider, skill_active`
          )
          .not("full_name", "is", null)
          .neq("full_name", "")
          .eq("skill_provider", true)
          .or("reference_status.is.null,reference_status.eq.confirmed")
          .or("account_status.is.null,account_status.neq.deleted");

        if (selectedLocation !== "All") {
          query = query.eq("location", selectedLocation);
        }

        if (searchQuery && searchQuery.trim() !== "") {
          const searchTerm = `%${searchQuery}%`;
          query = query.or(
            `full_name.ilike.${searchTerm},` +
              `location.ilike.${searchTerm},` +
              `church_name.ilike.${searchTerm},` +
              `bio2.ilike.${searchTerm},` +
              `bio1.ilike.${searchTerm}`
          );
        }

        const { data, error } = await query;
        if (error) throw error;

        const seen = new Set();
        let filteredUsers = (data || [])
          .filter((user) => {
            if (seen.has(user.user_id)) return false;
            seen.add(user.user_id);
            return true;
          })
          .filter(
            (user) =>
              user.bio1 ||
              (user.ways_to_help && user.ways_to_help.length > 0)
          );

        if (selectedCategory !== "All") {
          filteredUsers = filteredUsers.filter((user) => {
            const categoryLower = selectedCategory.toLowerCase();

            if (user.ways_to_help?.some((w) => w.toLowerCase() === categoryLower))
              return true;
            if (user.bio21?.toLowerCase().includes(categoryLower))
              return true;
            if (
              user.service_types?.some(
                (t) =>
                  t.toLowerCase() === categoryLower ||
                  t.toLowerCase().includes(categoryLower)
              )
            )
              return true;

            return false;
          });
        }

        setUsers(filteredUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      }

      setLoading(false);
    };

    fetchUsers();
  }, [searchQuery, selectedCategory, selectedLocation]);

  useEffect(() => {
    const fetchSavedIds = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("saved_profiles")
        .select("saved_user_id")
        .eq("user_id", user.id);
      if (data) setSavedIds(new Set(data.map((r) => r.saved_user_id)));
    };
    fetchSavedIds();
  }, []);

  const handleToggleSave = useCallback(async (profileUserId, e) => {
    e.stopPropagation();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    setSavingId(profileUserId);
    if (savedIds.has(profileUserId)) {
      await supabase
        .from("saved_profiles")
        .delete()
        .eq("user_id", user.id)
        .eq("saved_user_id", profileUserId);
      setSavedIds((prev) => { const next = new Set(prev); next.delete(profileUserId); return next; });
    } else {
      await supabase
        .from("saved_profiles")
        .insert({ user_id: user.id, saved_user_id: profileUserId });
      setSavedIds((prev) => new Set([...prev, profileUserId]));
    }
    setSavingId(null);
  }, [savedIds]);

  const handleViewProfile = (userId) => navigate(`/profile/${userId}`);
  const handleInputChange = (e) => setSearchQuery(e.target.value);
  const handleCategoryChange = (category) => setSelectedCategory(category);
  const handleLocationChange = (e) => setSelectedLocation(e.target.value);

  const getDisplayTags = (user) => {
    const tags = [];
    if (user.ways_to_help?.length) {
      tags.push(...user.ways_to_help);
    } else if (user.service_types?.length) {
      tags.push(...user.service_types);
    }
    return tags.slice(0, 3);
  };

  const dm = isDarkMode ? "dark" : "";

  return (
    <div className={`explore-root${dm ? " dark" : ""}`}>
      <Navbar />

      {/* ── Hero / Header zone ── */}
      <div className={`explore-hero${dm ? " dark" : ""}`}>
        <div className="explore-hero-inner">
          <h1 className={`explore-heading${dm ? " dark" : ""}`}>
            Find someone to help
          </h1>

          {/* Search row */}
          <div className="explore-search-row">
            <div className="explore-search-wrap">
              <svg
                className={`explore-search-icon${dm ? " dark" : ""}`}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                className={`explore-search-input${dm ? " dark" : ""}`}
                placeholder="Search by name, skill, or church…"
                value={searchQuery}
                onChange={handleInputChange}
              />
              {searchQuery && (
                <button
                  className={`explore-search-clear${dm ? " dark" : ""}`}
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>

            {locations.length > 0 && (
              <select
                className={`explore-location-select${dm ? " dark" : ""}`}
                value={selectedLocation}
                onChange={handleLocationChange}
              >
                <option value="All">All locations</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Category pills */}
          <div className="explore-pills">
            {serviceCategories.map((cat) => (
              <button
                key={cat.value}
                className={`explore-pill${selectedCategory === cat.value ? " active" : ""}${dm ? " dark" : ""}`}
                onClick={() => handleCategoryChange(cat.value)}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Results area ── */}
      <div className="explore-results-area">
        <div className="explore-col">

          {/* Result count */}
          {!loading && (
            <p className={`explore-count${dm ? " dark" : ""}`}>
              {users.length} {users.length === 1 ? "person" : "people"} found
              {selectedCategory !== "All" && ` · ${selectedCategory}`}
              {selectedLocation !== "All" && ` · ${selectedLocation}`}
            </p>
          )}

          {/* Cards */}
          {loading ? (
            <div className="explore-card-list">
              {[1, 2, 3, 4].map((n) => (
                <SkeletonCard key={n} isDarkMode={isDarkMode} />
              ))}
            </div>
          ) : users.length > 0 ? (
            <div className="explore-card-list">
              {users.map((user, index) => {
                const tags = getDisplayTags(user);
                const extraTags =
                  (user.ways_to_help?.length || user.service_types?.length || 0) - tags.length;

                return (
                  <article
                    key={`${user.user_id}-${index}`}
                    className={`explore-card${dm ? " dark" : ""}`}
                    onClick={() => handleViewProfile(user.user_id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleViewProfile(user.user_id)
                    }
                  >
                    {/* Avatar */}
                    <div className={`explore-avatar${dm ? " dark" : ""}`}>
                      {user.profile_picture_url ? (
                        <img
                          src={user.profile_picture_url}
                          alt={`${user.full_name}'s profile`}
                          className="explore-avatar-img"
                        />
                      ) : (
                        <span className="explore-avatar-initial">
                          {user.full_name?.charAt(0) || "?"}
                        </span>
                      )}
                    </div>

                    {/* Body */}
                    <div className="explore-card-body">
                      <div className="explore-card-top">
                        <div className="explore-card-meta">
                          <h3 className={`explore-card-name${dm ? " dark" : ""}`}>
                            {user.full_name}
                          </h3>
                        </div>
                        <button
                          className={`explore-view-btn${dm ? " dark" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewProfile(user.user_id);
                          }}
                        >
                          View
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </button>
                      </div>

                      {user.bio1 && (
                        <p className={`explore-card-tagline${dm ? " dark" : ""}`}>
                          {user.bio1}
                        </p>
                      )}

                      {(user.church_name || user.location) && (
                        <p className={`explore-card-church${dm ? " dark" : ""}`}>
                          {user.church_name}
                          {user.church_name && user.location && " · "}
                          {user.location}
                        </p>
                      )}

                      {user.bio2 && (
                        <p className={`explore-card-bio${dm ? " dark" : ""}`}>
                          {user.bio2}
                        </p>
                      )}

                      {tags.length > 0 && (
                        <div className="explore-card-tags">
                          {tags.map((tag) => (
                            <span
                              key={tag}
                              className={`explore-tag${dm ? " dark" : ""}`}
                            >
                              {tag}
                            </span>
                          ))}
                          {extraTags > 0 && (
                            <span className={`explore-tag extra${dm ? " dark" : ""}`}>
                              +{extraTags} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className={`explore-empty${dm ? " dark" : ""}`}>
              <svg
                width="72"
                height="72"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`explore-empty-icon${dm ? " dark" : ""}`}
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <h3 className={`explore-empty-title${dm ? " dark" : ""}`}>
                {searchQuery || selectedCategory !== "All" || selectedLocation !== "All"
                  ? "No matches found"
                  : "No people available yet"}
              </h3>
              <p className={`explore-empty-msg${dm ? " dark" : ""}`}>
                {searchQuery || selectedCategory !== "All" || selectedLocation !== "All"
                  ? "Try adjusting your filters or search term"
                  : "Check back soon as more people join the platform"}
              </p>
              {(searchQuery || selectedCategory !== "All" || selectedLocation !== "All") && (
                <button
                  className={`explore-empty-btn${dm ? " dark" : ""}`}
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("All");
                    setSelectedLocation("All");
                  }}
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Explore;
